import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  model,
  signal,
  viewChild,
} from "@angular/core";
import { ResourcePipe } from "@app/kernel/tools/download-files/downloaded-files.config";
import { FileModel } from "../../helpers/file-model";
import { EFileStatus } from "../../interfaces/file-status.enum";
import { TranslateModule } from "@ngx-translate/core";
import { SvgIconComponent } from "@kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { TImagePreviewResolveUrlFn } from "../../interfaces/image-preview.interfaces";
import { FileService } from "../../services/file.service";

/**
 * Image preview (lightbox) component with zoom/pan controls and loading states.
 *
 * Opened via {@link ImagePreviewService} which dynamically creates and attaches this component.
 *
 * Features:
 * - Zoom in/out (buttons, keyboard shortcuts, wheel zoom)
 * - Fit-to-screen + reset
 * - Drag-to-pan (useful when zoomed in)
 * - Loading skeleton/spinner + error state with retry
 * - Download button
 **/
@Component({
  selector: "sxw-image-preview",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./image-preview.component.html",
  styleUrls: ["./image-preview.component.scss"],
  providers: [ResourcePipe],
  imports: [TranslateModule, SvgIconComponent],
})
export class ImagePreviewComponent {
  /** Controls whether the preview overlay is visible. */
  readonly opened = model<boolean>(false);

  /** The file to preview. */
  readonly file = input<FileModel | string | null>(null);

  /** Translation key for the image alt text. */
  readonly imageAlt = input<string>("IMAGE_PREVIEW.ALT.DEFAULT");

  /** Extra arguments passed to ResourcePipe.transform. */
  readonly resourcePipeOptions = input<unknown[]>([]);

  /** Resolves a file key into a usable image URL. */
  readonly resolveUrl = input<TImagePreviewResolveUrlFn>((key, defaultResolve) => defaultResolve(key));

  /** Minimum zoom factor allowed. */
  readonly minZoom = input(0.2);

  /** Maximum zoom factor allowed. */
  readonly maxZoom = input(6);

  /** The zoom factor */
  readonly zoomFactor = input(1.12);

  private readonly resourcePipe = inject(ResourcePipe);
  private readonly fileService = inject(FileService);

  /** Current zoom factor. */
  readonly zoom = signal<number>(1);

  /** Pan offsets in pixels. */
  readonly panX = signal<number>(0);
  readonly panY = signal<number>(0);

  /** Whether the image is currently loading. */
  readonly isLoading = signal<boolean>(true);

  /** Whether the last load attempt failed. */
  readonly loadError = signal<boolean>(false);

  readonly stageRef = viewChild<ElementRef<HTMLElement>>("stage");
  readonly imgRef = viewChild<ElementRef<HTMLImageElement>>("imgEl");

  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private panOriginX = 0;
  private panOriginY = 0;

  readonly src = computed<string | null>(() => {
    if (this.loadError()) {
      return null;
    }

    const f = this.file();
    if (!f) return null;

    const defaultResolve = (key: string) => this.resourcePipe.transform(key, ...this.resourcePipeOptions());

    if (typeof f === "string") return f ? this.resolveUrl()(f, defaultResolve) : null;

    if (f.state !== EFileStatus.SAVED) return f.base64?.toString() ?? null;

    const key = f.key ?? "";
    return key ? this.resolveUrl()(key, defaultResolve) : null;
  });

  readonly transform = computed<string>(() => {
    return `translate(-50%, -50%) translate(${this.panX()}px, ${this.panY()}px) scale(${this.zoom()})`;
  });

  readonly zoomPercent = computed<number>(() => Math.round(this.zoom() * 100));

  constructor() {
    effect(() => {
      const currentSrc = this.src();

      if (!currentSrc) {
        this.isLoading.set(false);
        this.loadError.set(false);
        return;
      }

      this.isLoading.set(true);
      this.loadError.set(false);
      this.resetView();
    });

    effect(() => {
      if (!this.opened()) return;

      queueMicrotask(() => {
        if (this.opened() && !this.isLoading() && !this.loadError()) this.fitToScreen();
      });
    });
  }

  close(): void {
    this.opened.set(false);
  }

  onContainerKeydown(event: KeyboardEvent): void {
    if (event.code === "Space") event.preventDefault();

    const key = event.key.toLowerCase();

    if (key === "+" || event.key === "=" /* shift+= */) {
      event.preventDefault();
      this.zoomIn();
      return;
    }

    if (key === "-") {
      event.preventDefault();
      this.zoomOut();
      return;
    }

    if (key === "0") {
      event.preventDefault();
      this.resetView();
      return;
    }

    if (key === "f") {
      event.preventDefault();
      this.fitToScreen();
      return;
    }
  }

  onImageLoad(): void {
    this.isLoading.set(false);
    this.loadError.set(false);
    this.fitToScreen();
  }

  onImageError(): void {
    this.isLoading.set(false);
    this.loadError.set(true);
  }

  retryLoad(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.resetView();
  }

  zoomIn(): void {
    this.setZoom(this.zoom() * 1.15);
  }

  zoomOut(): void {
    this.setZoom(this.zoom() / 1.15);
  }

  resetView(): void {
    this.zoom.set(1);
    this.panX.set(0);
    this.panY.set(0);
  }

  fitToScreen(): void {
    const stageEl = this.stageRef()?.nativeElement;
    const imgEl = this.imgRef()?.nativeElement;

    if (!stageEl || !imgEl || !imgEl.naturalWidth || !imgEl.naturalHeight) return;

    const rect = stageEl.getBoundingClientRect();
    const { paddingLeft, paddingRight, paddingTop, paddingBottom } = getComputedStyle(stageEl);

    const paddingW = parseFloat(paddingLeft) + parseFloat(paddingRight);
    const paddingH = parseFloat(paddingTop) + parseFloat(paddingBottom);

    const maxW = rect.width - paddingW;
    const maxH = rect.height - paddingH;

    const scaleX = maxW / imgEl.naturalWidth;
    const scaleY = maxH / imgEl.naturalHeight;

    const nextZoom = this.clamp(Math.min(scaleX, scaleY), this.minZoom(), this.maxZoom());
    this.zoom.set(nextZoom);
    this.panX.set(0);
    this.panY.set(0);
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();

    const stageEl = this.stageRef()?.nativeElement;
    if (!stageEl) return;

    const zoomFactor = this.zoomFactor();
    const oldZoom = this.zoom();
    const factor = event.deltaY < 0 ? zoomFactor : 1 / zoomFactor;

    const nextZoom = this.clamp(oldZoom * factor, this.minZoom(), this.maxZoom());
    if (nextZoom === oldZoom) return;

    this.zoom.set(nextZoom);

    const rect = stageEl.getBoundingClientRect();
    const cursorX = event.clientX - rect.left - rect.width / 2;
    const cursorY = event.clientY - rect.top - rect.height / 2;
    const zoomRatio = nextZoom / oldZoom;
    const newPanX = cursorX - (cursorX - this.panX()) * zoomRatio;
    const newPanY = cursorY - (cursorY - this.panY()) * zoomRatio;

    this.panX.set(newPanX);
    this.panY.set(newPanY);
  }

  onPanStart(event: MouseEvent): void {
    this.isPanning = true;
    this.panStartX = event.clientX;
    this.panStartY = event.clientY;
    this.panOriginX = this.panX();
    this.panOriginY = this.panY();
  }

  onPanMove(event: MouseEvent): void {
    if (!this.isPanning) return;

    const dx = event.clientX - this.panStartX;
    const dy = event.clientY - this.panStartY;

    this.panX.set(this.panOriginX + dx);
    this.panY.set(this.panOriginY + dy);
  }

  onPanEnd(): void {
    this.isPanning = false;
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    if (!this.opened()) return;
    if (this.isLoading() || this.loadError()) return;
    this.fitToScreen();
  }

  @HostListener("window:keydown.escape")
  onOverlayKeydown(): void {
    this.close();
  }

  downloadFile(): void {
    const f = this.file();
    if (!f) return;

    const key = typeof f === "string" ? f : f.key;
    if (key) this.fileService.downloadFile(key).subscribe();
  }

  private setZoom(next: number): void {
    const clamped = this.clamp(next, this.minZoom(), this.maxZoom());
    this.zoom.set(clamped);

    if (clamped <= 1) {
      this.panX.set(0);
      this.panY.set(0);
    }
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }
}
